import { useMemo, useState, type FormEvent } from 'react'
import { Head, router } from '@inertiajs/react'
import AppShell, { type AppShellProps } from '@/components/AppShell'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ArrowLeft, Loader2, Users } from 'lucide-react'

interface Props extends Omit<AppShellProps, 'children'> {
  event: { id: number; name: string }
  userTypes: string[]
  defaults: {
    prefix: string
    startNumber: number
    count: number
    userType: string | null
  }
  urls: AppShellProps['urls'] & {
    bulkCreate: string
    eventUsers: string
  }
  translations: AppShellProps['translations'] & {
    bulkCreateUsers: string
    namePrefix: string
    namePrefixHint: string
    startingNumber: string
    count: string
    userType: string
    preview: string
    createUsers: string
    back: string
    pleaseSelect: string
  }
}

function buildPreview(prefix: string, startNumber: number, count: number) {
  if (!prefix.trim() || count < 1 || startNumber < 1) return ''

  const first = `${prefix.trim()}${startNumber}`
  if (count === 1) return first

  const last = `${prefix.trim()}${startNumber + count - 1}`
  if (count === 2) return `${first}, ${last}`

  return `${first}, ${prefix.trim()}${startNumber + 1}, … ${last}`
}

export default function BulkCreate(props: Props) {
  const { event, userTypes, defaults, urls, translations: t, ...shell } = props
  const [prefix, setPrefix] = useState(defaults.prefix)
  const [startNumber, setStartNumber] = useState(String(defaults.startNumber))
  const [count, setCount] = useState(String(defaults.count))
  const [userType, setUserType] = useState(defaults.userType || '')
  const [submitting, setSubmitting] = useState(false)

  const preview = useMemo(() => {
    const parsedStart = Number.parseInt(startNumber, 10)
    const parsedCount = Number.parseInt(count, 10)
    if (Number.isNaN(parsedStart) || Number.isNaN(parsedCount)) return ''
    return buildPreview(prefix, parsedStart, parsedCount)
  }, [prefix, startNumber, count])

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setSubmitting(true)

    router.post(
      urls.bulkCreate,
      {
        prefix,
        start_number: startNumber,
        count,
        user_type: userType,
      },
      {
        onFinish: () => setSubmitting(false),
      },
    )
  }

  return (
    <AppShell {...shell} urls={urls} translations={t}>
      <Head title={`${t.bulkCreateUsers} — ${event.name}`} />

      <div className="mx-auto max-w-xl py-10 px-4">
        <Button
          variant="ghost"
          size="sm"
          className="mb-6 text-[#91918E]"
          onClick={() => router.visit(urls.eventUsers)}
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t.back}
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-[#37352F]">{t.bulkCreateUsers}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="prefix">{t.namePrefix}</Label>
                <Input
                  id="prefix"
                  value={prefix}
                  onChange={(e) => setPrefix(e.target.value)}
                  placeholder="Guest-"
                  required
                />
                <p className="text-xs text-[#91918E]">{t.namePrefixHint}</p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="startNumber">{t.startingNumber}</Label>
                  <Input
                    id="startNumber"
                    type="number"
                    min={1}
                    value={startNumber}
                    onChange={(e) => setStartNumber(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="count">{t.count}</Label>
                  <Input
                    id="count"
                    type="number"
                    min={1}
                    max={500}
                    value={count}
                    onChange={(e) => setCount(e.target.value)}
                    required
                  />
                </div>
              </div>

              {userTypes.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="userType">{t.userType}</Label>
                  <select
                    id="userType"
                    value={userType}
                    onChange={(e) => setUserType(e.target.value)}
                    required
                    className="flex h-9 w-full rounded-md border border-[#E8E8E5] bg-white px-3 py-1 text-sm text-[#37352F] transition-colors focus:outline-none focus:ring-1 focus:ring-[#2383E2]"
                  >
                    <option value="">{t.pleaseSelect}</option>
                    {userTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {preview && (
                <div className="rounded-md border border-[#E8E8E5] bg-[#F7F7F5] px-4 py-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-[#91918E]">{t.preview}</p>
                  <p className="mt-1 text-sm text-[#37352F]">{preview}</p>
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-[#2383E2] hover:bg-[#1b6ec2]"
                disabled={submitting}
              >
                {submitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Users className="mr-2 h-4 w-4" />
                )}
                {t.createUsers}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  )
}
